/**
 * The order chain's Payment, Release, Production and Delivery step panels (Ben, 3 Oct; the
 * journeys branch's rounds 16b–16c). Each is the rail's own rows — node ·
 * label · name · status · the step's one outlined button · an ⓘ for the
 * rest — one line per item, no heading rows. The order page opens one at a
 * time under the rail, at `?step=…&line=…&version=…`, the focused line
 * marked and scrolled to. (The Order step's panel is `LinesStepPanel`.)
 *
 * Every word is the backend's: the production milestone's phrase and note,
 * the delivery line's status label, the line's used-in sentence.
 */
import { Link } from "react-router";
import type {
  DeliveryGroupView,
  DeliveryLineView,
  OrderLineView,
  OrderProformaView,
  OrderView,
  ProformaStateName,
} from "@shared-core/data/order-to-invoice-types";
import { NOT_YET_PRICED } from "@shared-core/domain/checkout-delivery";
import type { RailDetailItem } from "@shared-core/domain/lifecycle-rail";
import {
  linesReadyToRelease,
  railFromOrder,
  releaseItemsOf,
  releasedCount,
} from "@shared-core/domain/order-lifecycle-rail";
import {
  deliveryDestinationLabel,
  deliveryDestinationShort,
  deliveryLineProgress,
  despatchCarrierAndTracking,
  latestDespatch,
  recordableDeliveryLine,
  versionLabelOf,
} from "@shared-core/domain/order-to-invoice-presentation";
import {
  formatQuotedPrice,
  formatShortDateOrDash,
} from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import { InfoTip } from "../../patterns/InfoTip";
import { RailRow, RailRows } from "../../patterns/LifecycleRail";
import { ObjectChip } from "../../patterns/ObjectChip";
import { Button } from "../../primitives/button";
import { RecordStep, Tracking } from "./DeliveryLinesTable";
import { LineDespatches } from "./LineDespatches";
import { despatchProgressStatus } from "@shared-core/domain/despatch-progress";
import { PlannedOrderRowGroups } from "./PlannedOrderSections";
import {
  DetailItem,
  PanelHeading,
  ViewDetail,
  nodeOfTone,
  useFocusRef,
} from "./StepPanelParts";
import { ReleaseSwitches } from "./ReleaseSwitches";

const short = formatShortDateOrDash;

// ── Release ───────────────────────────────────────────────────────────────

export interface ReleaseStepHandlers {
  busy: boolean;
  /** Open the Release dialog for one line. */
  onRelease: (lineRef: string) => void;
  /** "Release all ready lines (N)". */
  onReleaseAll: () => void;
  /** *Place now?* on a line held to place together. */
  onPlaceNow: (lineRef: string) => void;
  /** *Change supplier…* on a line whose purchase order line was cancelled, or whose release didn't complete. */
  onChangeSupplier: (lineRef: string) => void;
  /** *Cancel line* on a released line whose purchase order line was cancelled (Q7). */
  onCancelLine: (lineRef: string) => void;
  /** Turn the order's "Review before release" on or off (V1 completion O). */
  onSetReview: (isOn: boolean) => void;
  /** Turn the order's "Deliver together" on or off (V1 completion O). */
  onSetDeliverTogether: (isOn: boolean) => void;
}

/**
 * The Release step's panel (round 19's `ReleaseStepPanel`; doc 57 §2): one
 * row per line with a gate of its own, its status as orders words it, and
 * its one step — Release…, Review a held line, *Place now* on a line held to
 * place together, or *Change supplier…* / *Cancel line* once its purchase
 * order line was cancelled (Q7). "N of M released" is the rail's own count.
 */
export function ReleaseStepPanel({
  order,
  focusLineNo,
  handlers,
  lineChips,
}: {
  order: OrderView;
  focusLineNo: string | null;
  handlers: ReleaseStepHandlers;
  /** A line's Needs attention chips (v1 completion I §10), on its row. */
  lineChips?: (lineRef: string) => React.ReactNode;
}) {
  const focusRef = useFocusRef();
  const ready = linesReadyToRelease(order);
  // Orders' Release rows (its Release step), and a bought service's row for Change supplier….
  const releaseItems = releaseItemsOf(order);
  const gated = order.lines.flatMap((line) => {
    const item = releaseItems.get(line.line_ref) ?? boughtServiceItem(line);
    return item && line.release ? [{ line, item, release: line.release }] : [];
  });
  const { released, gated: total } = releasedCount(order);
  return (
    <div className="space-y-2" data-section-id="order.release">
      <PanelHeading
        title="Release"
        info="A line goes to its supplier or to production as soon as its checks pass, unless Review before release is on: then someone confirms the file and the brief match what was ordered and presses Release. A bought line goes on the supplier's purchase order and waits in Purchasing's To place; lines for the same supplier can be timed to arrive together. A service goes with the lines it applies to."
        infoLabel="About release"
        summary={`${released} of ${total} released`}
        action={
          ready.length >= 2 && (
            <Button
              size="sm"
              variant="outline"
              disabled={handlers.busy}
              onClick={handlers.onReleaseAll}
            >
              Release all ready lines ({ready.length})
            </Button>
          )
        }
      />
      <ReleaseSwitches
        review={order.release_review}
        deliverTogether={order.deliver_together_switch}
        handlers={{
          busy: handlers.busy,
          onSetReview: handlers.onSetReview,
          onSetDeliverTogether: handlers.onSetDeliverTogether,
        }}
      />
      <RailRows ariaLabel="Release">
        {gated.map(({ line, item, release }) => {
          const isFocused = line.line_no === focusLineNo;
          const target =
            release.to?.kind === "supplier"
              ? release.to.supplier_name
              : release.to?.kind === "stock"
                ? "stock"
                : "production";
          return (
            <RailRow
              key={line.line_ref}
              rowRef={isFocused ? focusRef : undefined}
              isFocused={isFocused}
              state={item.state}
              problem={item.problem}
              label={line.line_no}
              name={line.name.title}
              status={
                release.state === "released" || !target
                  ? item.status
                  : `${item.status} · to ${target}`
              }
              actions={
                <>
                  {lineChips?.(line.line_ref)}
                  <ReleaseRowActions line={line} handlers={handlers} />
                </>
              }
            />
          );
        })}
      </RailRows>
    </div>
  );
}

/**
 * A bought service that goes with its print (supplier interface spec 12) has
 * no gate of its own, but its supplier can change: a row of its own, so
 * *Change supplier…* sits there as it does in an item's Release dialog.
 */
function boughtServiceItem(line: OrderLineView): RailDetailItem | null {
  const r = line.release;
  if (r?.state !== "follows" || !r.change_supplier) return null;
  return {
    id: line.line_ref,
    label: line.line_no,
    name: line.name.title,
    state: "to_do",
    problem: null,
    status: r.status_label,
    link: null,
  };
}

function ReleaseRowActions({
  line,
  handlers,
}: {
  line: OrderLineView;
  handlers: ReleaseStepHandlers;
}) {
  const r = line.release!;
  const button = (label: string, aria: string, onClick: () => void) => (
    <Button
      key={label}
      size="sm"
      variant="outline"
      disabled={handlers.busy}
      aria-label={`${aria} ${line.line_no}`}
      onClick={onClick}
    >
      {label}
    </Button>
  );
  if (r.place_now_url)
    return button("Place now", "Place now", () =>
      handlers.onPlaceNow(line.line_ref),
    );
  if (r.po_line_cancelled || r.release_incomplete)
    return (
      <span className="flex flex-wrap gap-2">
        {r.change_supplier &&
          button("Change supplier…", "Change supplier of", () =>
            handlers.onChangeSupplier(line.line_ref),
          )}
        {r.po_line_cancelled &&
          line.cancel_url &&
          button("Cancel line", "Cancel line", () =>
            handlers.onCancelLine(line.line_ref),
          )}
      </span>
    );
  if (r.state === "follows")
    return r.change_supplier
      ? button("Change supplier…", "Change supplier of", () =>
          handlers.onChangeSupplier(line.line_ref),
        )
      : null;
  if (r.state === "released") return null;
  if (!r.release_url && !r.hold_url) return null;
  // A ready line with no Release is releasing itself: the one thing a person can do is hold it.
  const label =
    r.state === "held"
      ? "Review"
      : r.release_url
        ? "Release…"
        : r.state === "ready"
          ? "Hold…"
          : "Release…";
  return button(label, label, () => handlers.onRelease(line.line_ref));
}

// ── Production ────────────────────────────────────────────────────────────

/** A bought line's purchase order, as a chip to its page; the supplier beside it. */
function MadeBy({ line }: { line: OrderLineView }) {
  const p = line.production;
  if (!p) return <span className="text-muted-foreground">—</span>;
  const supplier = p.supplier_name ?? p.supplier_no ?? undefined;
  if (!p.purchase_order_no) return <>{supplier ?? "—"}</>;
  return (
    <ObjectChip
      label={p.purchase_order_no}
      detail={supplier}
      to={paths.purchaseOrderDetail({ purchaseOrderNo: p.purchase_order_no })}
    />
  );
}

/**
 * The Production step's panel: one row per line with something to produce,
 * its rail item (what produces it and where that stands), the purchase
 * order and the supplier's reference and despatch under the ⓘ.
 */
export function ProductionStepPanel({
  order,
  focusLineNo,
}: {
  order: OrderView;
  focusLineNo: string | null;
}) {
  const focusRef = useFocusRef();
  const items = new Map<string, RailDetailItem>(
    (
      railFromOrder(order).steps.find((s) => s.id === "production")?.detail ??
      []
    ).map((item) => [item.id, item]),
  );
  const made = order.lines.flatMap((line) => {
    const item = items.get(line.line_ref);
    return item ? [{ line, item }] : [];
  });
  return (
    <div className="space-y-2" data-section-id="order.production">
      <PanelHeading
        title="Production"
        summary={`${made.filter((m) => m.item.state === "done").length} of ${made.length} done`}
      />
      <RailRows ariaLabel="Production">
        {made.map(({ line, item }) => {
          const isFocused = line.line_no === focusLineNo;
          const p = line.production;
          return (
            <RailRow
              key={line.line_ref}
              rowRef={isFocused ? focusRef : undefined}
              isFocused={isFocused}
              state={item.state}
              problem={item.problem}
              label={item.label}
              name={
                p?.purchase_order_no ? (
                  <Link
                    to={paths.purchaseOrderDetail({
                      purchaseOrderNo: p.purchase_order_no,
                    })}
                    className="hover:underline"
                  >
                    {item.name}
                  </Link>
                ) : (
                  item.name
                )
              }
              spokenName={p?.purchase_order_no ? item.name : undefined}
              status={item.status}
              detail={
                <ViewDetail
                  title={`Production of ${line.line_no} ${line.name.title}`}
                >
                  <dl className="grid grid-cols-2 gap-x-8 gap-y-3">
                    <DetailItem label="What" wide>
                      {line.name.full}
                    </DetailItem>
                    <DetailItem label="By">
                      <MadeBy line={line} />
                    </DetailItem>
                    {(line.tharstern_job_no || line.tharstern_job_label) && (
                      <DetailItem label="Tharstern job">
                        {line.tharstern_job_no ?? line.tharstern_job_label}
                      </DetailItem>
                    )}
                    {p?.supplier_reference && (
                      <DetailItem label="Supplier's reference">
                        {p.supplier_reference}
                      </DetailItem>
                    )}
                    {p?.despatch && (
                      <DetailItem label="Despatched">
                        {short(p.despatch.despatched_on)}
                        {p.despatch.carrier && ` · ${p.despatch.carrier}`}
                        {p.despatch.tracking_reference &&
                          ` ${p.despatch.tracking_reference}`}
                      </DetailItem>
                    )}
                    <DetailItem label="Where it stands" wide>
                      {item.status || "—"}
                    </DetailItem>
                  </dl>
                </ViewDetail>
              }
            />
          );
        })}
      </RailRows>
    </div>
  );
}

// ── Delivery ──────────────────────────────────────────────────────────────

function DeliveryDetail({
  line,
  supplier,
}: {
  line: DeliveryLineView;
  supplier: string | null;
}) {
  const despatches = line.despatches ?? [];
  return (
    <dl className="grid grid-cols-2 gap-x-8 gap-y-3">
      <DetailItem label="Going to" wide>
        {deliveryDestinationLabel(line.destination)}
      </DetailItem>
      <DetailItem label="Tracking">
        <Tracking line={line} />
      </DetailItem>
      <DetailItem label="Arrived">
        {line.completed_quantity > 0 && line.status_on
          ? `${line.completed_quantity.toLocaleString("en-GB")} on ${short(line.status_on)}`
          : "Not yet"}
      </DetailItem>
      <DetailItem label="Recorded by">{line.completed_by ?? "—"}</DetailItem>
      {supplier && <DetailItem label="Supplier">{supplier}</DetailItem>}
      {despatches.length > 0 && (
        <DetailItem label="Despatches" wide>
          <ul className="space-y-1">
            {despatches.map((d, index) => (
              <li key={index} className="flex flex-wrap gap-x-1">
                <span className="tabular-nums">
                  {d.quantity.toLocaleString("en-GB")} on{" "}
                  {short(d.despatched_on)}
                </span>
                <span className="text-muted-foreground">·</span>
                {d.tracking_url && d.tracking_number ? (
                  <span>
                    {d.carrier_label}{" "}
                    <a
                      href={d.tracking_url}
                      target="_blank"
                      rel="noreferrer"
                      className="tabular-nums underline"
                    >
                      {d.tracking_number}
                    </a>
                  </span>
                ) : (
                  <span>
                    {despatchCarrierAndTracking(d) ?? "Carrier not recorded"}
                  </span>
                )}
                {d.delivered_on && (
                  <span className="text-muted-foreground">
                    · Delivered on {short(d.delivered_on)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </DetailItem>
      )}
      {line.record_received_blocked_reason && (
        <DetailItem label="Why it can't be recorded here" wide>
          {line.record_received_blocked_reason}
        </DetailItem>
      )}
    </dl>
  );
}

/** A delivery line's node: done once all arrived, started once despatched. */
function deliveryNode(line: DeliveryLineView) {
  return nodeOfTone(
    line.outstanding_quantity === 0 ? "done" : "neutral",
    line.status !== "awaiting_despatch",
  );
}

/** A delivery line's status on its row: "Part despatched · 1,000 of 2,000 · 14 Oct · DX 1234567891". */
function deliveryRowStatus(line: DeliveryLineView): string {
  const latest = latestDespatch(line);
  const isOnItsWay =
    line.status === "part_despatched" || line.status === "despatched";
  return [
    line.status_label,
    deliveryLineProgress(line),
    line.status_on ? short(line.status_on) : null,
    isOnItsWay && latest ? despatchCarrierAndTracking(latest) : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/** A group's title: where it goes, and the service and courier when the charge records them ("Acme Leeds HQ · Next day · DPD"). */
function groupTitle(group: DeliveryGroupView): string {
  return [group.destination_name, group.service_level, group.courier_name]
    .filter(Boolean)
    .join(" · ");
}

/** A group's detail line: where it comes from, who receives it, and how ("From PrintCo · Jo Smith, 0113 496 0000 · Goods in at rear"). */
function groupDetail(group: DeliveryGroupView): string | null {
  const recipient = group.recipient;
  return (
    [
      group.origin?.name ? `From ${group.origin.name}` : null,
      recipient
        ? [recipient.name, recipient.phone].filter(Boolean).join(", ") || null
        : null,
      recipient?.delivery_instructions ?? null,
    ]
      .filter(Boolean)
      .join(" · ") || null
  );
}

/** *Change delivery…* on an item line, or a 🔒 with orders' reason its delivery can't change (doc 106 §8.1). */
export function ChangeDeliveryControl({
  line,
  busy,
  onChangeDelivery,
}: {
  line: OrderLineView;
  busy: boolean;
  onChangeDelivery: (lineRef: string) => void;
}) {
  if (line.change_delivery)
    return (
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={busy}
        aria-label={`Change delivery of ${line.line_no} ${line.name.title}`}
        onClick={() => onChangeDelivery(line.line_ref)}
      >
        Change delivery…
      </Button>
    );
  if (line.change_delivery_refusal)
    return (
      <InfoTip
        icon="lock"
        label={`Why the delivery of ${line.line_no} can't change`}
      >
        {line.change_delivery_refusal}
      </InfoTip>
    );
  return null;
}

/** A group's charge: "Included" with the bought line, "Collection", "Not yet priced" (never £0), or its cost and price. */
function groupCharge(group: DeliveryGroupView): string | null {
  const charge = group.charge;
  if (!charge) return null;
  if (charge.kind === "included") return "Included";
  if (charge.kind === "collection") return "Collection";
  if (charge.kind === "not_yet_priced") return NOT_YET_PRICED;
  const money = (pence: number | null) =>
    pence === null ? "—" : formatQuotedPrice(pence);
  return `Cost ${money(charge.cost_pence)} · ${money(charge.price_pence)}`;
}

/**
 * The Delivery step's panel (was the Delivery tab), grouped by delivery
 * charge (order to invoice doc 102 §9.1): each group is where it goes and
 * how — the destination, the service level, where it comes from, who
 * receives it, the charge — with *Record despatched* on a group leaving our
 * warehouse. Under it, one row per delivery line, its version in the label
 * (".01 · A Bristol"): how many to where, where it stands, and Record
 * delivered or Record received on the row; every despatch, tracking, arrival
 * and who recorded it under the ⓘ. A line used in its services, not
 * delivered itself, is a "used in" row after the groups.
 */
export function DeliveryStepPanel({
  order,
  focusLineNo,
  focusVersion = null,
  busy,
  onRequestRecord,
  onRecordDespatched,
  onAgreeDeliveryPrice,
  onChangeDelivery,
}: {
  order: OrderView;
  focusLineNo: string | null;
  /** Opened at one version of the line: only its rows are marked. */
  focusVersion?: string | null;
  busy: boolean;
  onRequestRecord: (line: DeliveryLineView) => void;
  /** Record despatched on a group: offered where it carries `record_despatched_together` or `record_made_despatched`. */
  onRecordDespatched?: (groupId: string) => void;
  /** *Agree price…* on a group whose charge is not yet priced: offered where it carries `agree_price_url`. */
  onAgreeDeliveryPrice?: (groupId: string) => void;
  /** *Change delivery…* (doc 106 §9.4): offered on an item line carrying `change_delivery`. */
  onChangeDelivery?: (lineRef: string) => void;
}) {
  const focusRef = useFocusRef();
  // Change delivery… sits on an item line's first delivery row only: it changes all of the line's.
  const offeredChange = new Set<string>();
  let hasScrolled = false;
  const focusOf = (lineNo: string, versionLabel: string | null) => {
    const isFocused =
      lineNo === focusLineNo &&
      (focusVersion === null || focusVersion === versionLabel);
    const rowRef = isFocused && !hasScrolled ? focusRef : undefined;
    if (isFocused) hasScrolled = true;
    return { isFocused, rowRef };
  };
  const deliveryLines = order.lines.flatMap((l) => l.delivery_lines);
  const complete = deliveryLines.filter(
    (d) => d.outstanding_quantity === 0,
  ).length;
  const lineOf = new Map(
    order.lines.flatMap((l) =>
      l.delivery_lines.map((d) => [d.id, { orderLine: l, line: d }] as const),
    ),
  );
  const usedIn = order.lines.filter(
    (l) => l.used_in_label && l.delivery_lines.length === 0,
  );
  const orderCharge = order.order_delivery_charge_pence;

  const deliveryRow = (l: OrderLineView, line: DeliveryLineView) => {
    const version =
      l.versions.length > 1
        ? l.versions.find((v) => v.label === line.version_label)
        : undefined;
    const label = version
      ? `${l.line_no} · ${versionLabelOf(version)}`
      : l.line_no;
    const name = `${line.quantity.toLocaleString("en-GB")} → ${deliveryDestinationShort(line.destination)}`;
    const supplier =
      l.production?.supplier_name ?? l.production?.supplier_no ?? null;
    // A bought line sent or arriving in parts reads by its parts, its deliveries under its row.
    const isFirstRowOfLine = !offeredChange.has(l.line_ref);
    offeredChange.add(l.line_ref);
    const parts = l.despatch_progress;
    const inParts =
      parts !== null &&
      parts !== undefined &&
      l.delivery_lines.length === 1 &&
      ["part_sent", "sent", "part_delivered"].includes(parts.state);
    return (
      <RailRow
        key={line.id}
        {...focusOf(l.line_no, line.version_label)}
        {...deliveryNode(line)}
        label={label}
        name={name}
        status={
          inParts && parts
            ? despatchProgressStatus(parts)
            : deliveryRowStatus(line)
        }
        expanded={
          inParts && parts && parts.deliveries.length > 0 ? (
            <LineDespatches
              progress={parts}
              deliveryLines={l.delivery_lines}
              busy={busy}
              onRequestRecord={onRequestRecord}
            />
          ) : undefined
        }
        actions={
          <span className="inline-flex flex-wrap items-center gap-2">
            {onChangeDelivery && isFirstRowOfLine && (
              <ChangeDeliveryControl
                line={l}
                busy={busy}
                onChangeDelivery={onChangeDelivery}
              />
            )}
            <RecordStep
              line={line}
              busy={busy}
              onRequestRecord={(recorded) =>
                onRequestRecord(recordableDeliveryLine(recorded))
              }
            />
          </span>
        }
        detail={
          <ViewDetail title={`Delivery ${label}: ${name}`}>
            <DeliveryDetail line={line} supplier={supplier} />
          </ViewDetail>
        }
      />
    );
  };

  return (
    <div className="space-y-3" data-section-id="order.delivery_lines">
      <PanelHeading
        title="Delivery"
        summary={`${complete} of ${deliveryLines.length} complete`}
      />
      {orderCharge !== null && orderCharge !== undefined && (
        <p className="text-sm text-muted-foreground">
          Delivery {formatQuotedPrice(orderCharge)} for the whole order
        </p>
      )}
      {deliveryLines.length === 0 && usedIn.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nothing to deliver on this order yet.
        </p>
      ) : (
        <>
          {order.delivery_groups.map((group) => (
            <div key={group.id} className="space-y-1">
              <PanelHeading
                level={4}
                title={groupTitle(group)}
                detail={groupDetail(group)}
                summary={groupCharge(group)}
                action={
                  <>
                    {group.charge?.agree_price_url && onAgreeDeliveryPrice && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        aria-label={`Agree the delivery price to ${group.destination_name}`}
                        onClick={() => onAgreeDeliveryPrice(group.id)}
                      >
                        Agree price…
                      </Button>
                    )}
                    {(group.record_despatched_together ||
                      (group.record_made_despatched ?? []).length > 0) &&
                      onRecordDespatched && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={busy}
                          aria-label={`Record despatched to ${group.destination_name}`}
                          onClick={() => onRecordDespatched(group.id)}
                        >
                          Record despatched
                        </Button>
                      )}
                  </>
                }
              />
              <RailRows ariaLabel={`Delivery to ${group.destination_name}`}>
                {group.delivery_line_ids.flatMap((id) => {
                  const found = lineOf.get(id);
                  return found
                    ? [deliveryRow(found.orderLine, found.line)]
                    : [];
                })}
              </RailRows>
            </div>
          ))}
          {usedIn.length > 0 && (
            <RailRows ariaLabel="Used in">
              {usedIn.map((l) => (
                <RailRow
                  key={`used-in-${l.line_ref}`}
                  {...focusOf(l.line_no, null)}
                  {...nodeOfTone(l.status.tone)}
                  label={l.line_no}
                  name={
                    <Link
                      to={paths.orderDetail({
                        orderNo: order.order_no,
                        step: "order",
                        line: l.used_in[0]?.line_no,
                      })}
                      className="underline decoration-muted-foreground/40 underline-offset-2 hover:decoration-current"
                    >
                      {l.quantity.toLocaleString("en-GB")} → {l.used_in_label}
                    </Link>
                  }
                  spokenName={l.used_in_label ?? undefined}
                  status={l.status.label}
                />
              ))}
            </RailRows>
          )}
        </>
      )}
      <PlannedOrderRowGroups where="delivery_step" />
    </div>
  );
}

// ── Payment ───────────────────────────────────────────────────────────────

export interface PaymentStepHandlers {
  busy: boolean;
  /** Open Change payment method. */
  onChangeMethod: () => void;
  /**
   * Start a card payment and copy its link (Copy pay link): the order's, or
   * with `addedLineRef` a later addition's own.
   */
  onCopyPayLink: (addedLineRef?: string) => void;
  /** View PDF of a pro-forma: fetch it (auth-gated) and save it. */
  onViewProformaPdf: (proforma: OrderProformaView) => void;
  /** Open Record payment on a pro-forma (finance only: it carries the URL). */
  onRecordPayment: (proforma: OrderProformaView) => void;
  /** Mark the excess refunded on an overpaid pro-forma (finance only: it carries the URL). */
  onMarkExcessRefunded: (proforma: OrderProformaView) => void;
  /** Resend the order's email to its customer, the pro-forma listed as owed (who may manage orders: it carries the URL). */
  onResend: (proforma: OrderProformaView) => void;
}

/** A pro-forma's state as the rail's node: paid is done, part paid at risk, expired late. */
const PROFORMA_TONE: Record<
  ProformaStateName,
  "done" | "at_risk" | "late" | "neutral"
> = {
  issued: "neutral",
  part_paid: "at_risk",
  paid: "done",
  superseded: "neutral",
  expired: "late",
  voided: "neutral",
};

/** "Part paid · £100.00 of £300.00": the ledger's state, with the money where it helps. */
function proformaStatus(p: OrderProformaView): string {
  if (p.state === "part_paid")
    return `${p.state_label} · ${formatQuotedPrice(p.paid_pence)} of ${formatQuotedPrice(p.gross_pence)}`;
  if (p.superseded_by) return `${p.state_label} (${p.superseded_by})`;
  return p.state_label;
}

/**
 * The Payment step's pro-formas (doc 50 §9 item 2, sales ledger SL3; round
 * 19's `PaymentStepPanel`): each with its gross and where it stands, View PDF
 * and, for finance while it still asks for money, Record payment; its lines,
 * how to pay it and the payments received against it behind its ⓘ. A
 * replaced, expired or voided one stays as history. Resend sends the order's
 * one customer email again (everything outstanding, a fresh link); the daily
 * per-order reminders chase it, so round 19's Chase is not built (Ben, 6 Oct).
 */
function ProformaRows({
  order,
  handlers,
}: {
  order: OrderView;
  handlers: PaymentStepHandlers;
}) {
  const billing = order.billing;
  if (!billing || billing.proformas.length === 0) return null;
  return (
    <RailRows ariaLabel="Pro-formas">
      {billing.proformas.map((p) => {
        const payments = billing.payments.filter(
          (pay) => pay.proforma_no === p.proforma_no,
        );
        return (
          <RailRow
            key={p.proforma_no}
            {...nodeOfTone(PROFORMA_TONE[p.state])}
            label={p.proforma_no}
            name={`Lines ${p.line_nos.join(", ")}`}
            status={
              <span className="inline-flex flex-wrap items-center gap-x-1.5">
                <span className="tabular-nums">
                  {formatQuotedPrice(p.gross_pence)}
                </span>
                <span aria-hidden>·</span>
                <span>{proformaStatus(p)}</span>
                <span aria-hidden>·</span>
                <span>issued {short(p.issued_on)}</span>
              </span>
            }
            actions={
              <span className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={handlers.busy}
                  onClick={() => handlers.onViewProformaPdf(p)}
                >
                  View PDF
                </Button>
                {p.resend_email_url && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={handlers.busy}
                    onClick={() => handlers.onResend(p)}
                  >
                    Resend
                  </Button>
                )}
                {p.record_payment_url && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={handlers.busy}
                    onClick={() => handlers.onRecordPayment(p)}
                  >
                    Record payment
                  </Button>
                )}
              </span>
            }
            expanded={
              p.overpaid_sentence ? (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pl-6">
                  <p
                    role="status"
                    className="text-sm text-amber-700 dark:text-amber-400"
                  >
                    {p.overpaid_sentence}
                  </p>
                  {p.mark_excess_refunded_url && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={handlers.busy}
                      onClick={() => handlers.onMarkExcessRefunded(p)}
                    >
                      Mark the excess refunded
                    </Button>
                  )}
                </div>
              ) : p.overpaid_refunded_on ? (
                <p className="text-muted-foreground pl-6 text-sm">
                  The excess of {formatQuotedPrice(p.overpaid_pence)} was marked
                  refunded in Xero on {short(p.overpaid_refunded_on)}.
                </p>
              ) : undefined
            }
            detail={
              <ViewDetail title={`Pro-forma ${p.proforma_no}`}>
                <dl className="grid grid-cols-2 gap-x-8 gap-y-3">
                  <DetailItem label="Where it stands">
                    {p.state_label}
                  </DetailItem>
                  <DetailItem label="Valid to">
                    {short(p.valid_until)}
                  </DetailItem>
                  <DetailItem label="Gross">
                    {formatQuotedPrice(p.gross_pence)}
                  </DetailItem>
                  <DetailItem label="Still owed">
                    {formatQuotedPrice(p.outstanding_pence)}
                  </DetailItem>
                  <DetailItem label="Payment reference">
                    {p.payment_reference}
                  </DetailItem>
                  <DetailItem label="Pay by card instead">
                    {p.is_card_allowed ? "Offered" : "Not offered"}
                  </DetailItem>
                </dl>
                {payments.length > 0 && (
                  <ul className="divide-y" aria-label="Payments received">
                    {payments.map((pay) => (
                      <li
                        key={`${pay.received_on}-${pay.reference ?? pay.amount_pence}`}
                        className="flex flex-wrap justify-between gap-x-4 py-1.5"
                      >
                        <span>
                          {pay.method_label} · received {short(pay.received_on)}
                          {pay.reference && ` · ${pay.reference}`}
                          {pay.invoice_no && ` · on ${pay.invoice_no}`}
                        </span>
                        <span className="tabular-nums">
                          {formatQuotedPrice(pay.amount_pence)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </ViewDetail>
            }
          />
        );
      })}
    </RailRows>
  );
}

/**
 * The Payment step's panel (doc 47 §3): how the order is paid and where that
 * stands, in orders' words — one row per decision (the order, then each line
 * added later), with its credit decision or card payment — then the steps:
 * Change payment method and, while the customer owes a card payment, Copy
 * pay link (the Stripe page to send them): the order's at the heading, an
 * addition's on its own row (3c). A cancelled line's card refund still owed
 * is said under the heading. Paying first (sales ledger SL3), the pro-formas
 * follow, with Record payment for finance.
 */
export function PaymentStepPanel({
  order,
  handlers,
}: {
  order: OrderView;
  handlers: PaymentStepHandlers;
}) {
  const payment = order.payment;
  if (!payment) return null;
  // The link is offered while any decision waits on a card payment; each
  // unpaid one gets its own: the order's at the heading, an addition's on its row.
  const canLink = payment.pay_link_url !== null;
  const ownSubject = payment.subjects.find((s) => !s.added_line_ref);
  const isOrderUnpaid = !ownSubject || !ownSubject.requirement_met;
  return (
    <div className="space-y-2">
      <PanelHeading
        title="Payment"
        info="How the payer pays and where that stands. On account, credit control decides when the order is placed; by card, the customer pays through the order's link; by pro-forma, the customer pays it by bank transfer (or by card through the order's link) before the work goes ahead. A pro-forma is not a VAT invoice: the invoice is raised once it is paid."
        infoLabel="About payment"
        summary={
          <span>
            {payment.method_label} · {payment.sentence}
          </span>
        }
        action={
          <span className="flex flex-wrap gap-2">
            {canLink && isOrderUnpaid && (
              <Button
                size="sm"
                variant="outline"
                disabled={handlers.busy}
                onClick={() => handlers.onCopyPayLink()}
              >
                Copy pay link
              </Button>
            )}
            {payment.change_method_url && (
              <Button
                size="sm"
                variant="outline"
                disabled={handlers.busy}
                onClick={handlers.onChangeMethod}
              >
                Change payment method
              </Button>
            )}
          </span>
        }
      />
      {payment.refunds_owed_sentence && (
        <p role="status" className="text-sm text-amber-700 dark:text-amber-400">
          {payment.refunds_owed_sentence}
        </p>
      )}
      <RailRows ariaLabel="Payment">
        {payment.subjects.map((s) => {
          const addedLineRef = s.added_line_ref;
          return (
            <RailRow
              key={s.label}
              {...nodeOfTone(s.requirement_met ? "done" : "neutral")}
              label={s.label}
              name={formatQuotedPrice(s.amount_pence)}
              status={
                <span className="inline-flex flex-wrap items-center gap-x-1.5">
                  <span>{s.credit_label ?? s.sentence}</span>
                  {s.paid_on && (
                    <>
                      <span aria-hidden>·</span>
                      <span>paid {short(s.paid_on)}</span>
                    </>
                  )}
                </span>
              }
              actions={
                canLink &&
                addedLineRef &&
                !s.requirement_met && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={handlers.busy}
                    aria-label={`Copy pay link for ${s.label}`}
                    onClick={() => handlers.onCopyPayLink(addedLineRef)}
                  >
                    Copy pay link
                  </Button>
                )
              }
            />
          );
        })}
      </RailRows>
      <ProformaRows order={order} handlers={handlers} />
    </div>
  );
}
